import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Grid,
  Column,
  InlineLoading,
  InlineNotification,
  Tile,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  TabPanels,
} from '@carbon/react';
import { Download, DocumentPdf, Document } from '@carbon/icons-react';
import './PactDownloads.scss';

interface PactDownloadsProps {
  surveyId: string;
  formState: any;
}

const PactDownloads: React.FC<PactDownloadsProps> = ({ surveyId, formState }) => {
  const { t } = useTranslation();
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isGeneratingJson, setIsGeneratingJson] = useState(false);
  const [pdfStatus, setPdfStatus] = useState<'none' | 'success' | 'error'>('none');
  const [jsonStatus, setJsonStatus] = useState<'none' | 'success' | 'error'>('none');
  const newBackendUrl = process.env.REACT_APP_NEW_BACKEND_URL;

  const handleGeneratePactPdf = async () => {
    setIsGeneratingPdf(true);
    setPdfStatus('none');
    
    try {
      // Create a payload with the current form state
      const payload = {
        surveyId: surveyId,
        surveyData: formState
      };
      
      // Send the data to the backend
      const pdfResponse = await fetch(`${newBackendUrl}/api/v1/pact/${surveyId}/pcf/pdf`, {
        method: 'POST',
        headers: {
          'Accept': 'application/pdf',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (pdfResponse.ok) {
        // Handle the PDF - download it directly
        const pdfBlob = await pdfResponse.blob();
        
        // Create a download link for the PDF
        const url = window.URL.createObjectURL(pdfBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `pact-pcf-${surveyId}.pdf`;
        document.body.appendChild(a);
        a.click();
        
        // Cleanup
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        setPdfStatus('success');
      } else {
        console.error('Failed to generate PACT PDF:', pdfResponse.statusText);
        setPdfStatus('error');
      }
    } catch (error) {
      console.error('Error generating PACT PDF:', error);
      setPdfStatus('error');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handleGeneratePactJson = async () => {
    setIsGeneratingJson(true);
    setJsonStatus('none');
    
    try {
      // Create a payload with the current form state
      const payload = {
        surveyId: surveyId,
        surveyData: formState
      };
      
      // Send the data to the backend
      const jsonResponse = await fetch(`${newBackendUrl}/api/v1/pact/${surveyId}/pcf/json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (jsonResponse.ok) {
        // Handle the JSON response
        const jsonData = await jsonResponse.json();
        
        // Convert JSON data to a downloadable blob
        const jsonBlob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
        
        // Create a download link for the JSON
        const url = window.URL.createObjectURL(jsonBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `pact-pcf-${surveyId}.json`;
        document.body.appendChild(a);
        a.click();
        
        // Cleanup
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        setJsonStatus('success');
      } else {
        console.error('Failed to generate PACT JSON:', jsonResponse.statusText);
        setJsonStatus('error');
      }
    } catch (error) {
      console.error('Error generating PACT JSON:', error);
      setJsonStatus('error');
    } finally {
      setIsGeneratingJson(false);
    }
  };

  return (
    <Grid>
      <Column sm={4} md={8} lg={16}>
        <h4>{t('survey.submission.pact.title', 'Product Carbon Footprint (PACT)')}</h4>
        <p className="pact-description">
          {t('survey.submission.pact.description', 'Download your product carbon footprint data in PACT-compliant format.')}
        </p>
        
        <Tile className="pact-tile">
          <h5>{t('survey.submission.pact.formats', 'Available Formats')}</h5>
          
          <Tabs>
            <TabList aria-label="PACT Download Formats">
              <Tab>{t('survey.submission.pact.pdfTab', 'PDF Report')}</Tab>
              <Tab>{t('survey.submission.pact.jsonTab', 'JSON Data')}</Tab>
            </TabList>
            
            <TabPanels>
              <TabPanel>
                <div className="pact-format-description">
                  <p>
                    {t('survey.submission.pact.pdfDescription', 
                      'Download a PDF report containing your product carbon footprint data in the PACT format.')}
                  </p>
                  
                  <div className="pact-button-container">
                    {isGeneratingPdf ? (
                      <InlineLoading
                        description={t('survey.submission.pact.generating', 'Generating PDF...')}
                        status="active"
                      />
                    ) : (
                      <Button
                        kind="tertiary"
                        renderIcon={DocumentPdf}
                        onClick={handleGeneratePactPdf}
                      >
                        {t('survey.submission.pact.generatePdf', 'Generate PDF')}
                      </Button>
                    )}
                  </div>
                  
                  {pdfStatus === 'success' && (
                    <InlineNotification
                      kind="success"
                      title={t('survey.submission.pact.pdfSuccess', 'PDF Generated')}
                      subtitle={t('survey.submission.pact.pdfSuccessDescription', 'Your PACT PCF PDF was successfully generated and downloaded.')}
                      hideCloseButton
                      lowContrast
                    />
                  )}
                  
                  {pdfStatus === 'error' && (
                    <InlineNotification
                      kind="error"
                      title={t('survey.submission.pact.pdfError', 'PDF Generation Failed')}
                      subtitle={t('survey.submission.pact.pdfErrorDescription', 'There was an error generating your PACT PCF PDF. Please try again.')}
                      hideCloseButton
                      lowContrast
                    />
                  )}
                </div>
              </TabPanel>
              
              <TabPanel>
                <div className="pact-format-description">
                  <p>
                    {t('survey.submission.pact.jsonDescription', 
                      'Download your product carbon footprint data as a PACT-compliant JSON file for integration with other systems.')}
                  </p>
                  
                  <div className="pact-button-container">
                    {isGeneratingJson ? (
                      <InlineLoading
                        description={t('survey.submission.pact.generatingJson', 'Generating JSON...')}
                        status="active"
                      />
                    ) : (
                      <Button
                        kind="tertiary"
                        renderIcon={Document}
                        onClick={handleGeneratePactJson}
                      >
                        {t('survey.submission.pact.generateJson', 'Generate JSON')}
                      </Button>
                    )}
                  </div>
                  
                  {jsonStatus === 'success' && (
                    <InlineNotification
                      kind="success"
                      title={t('survey.submission.pact.jsonSuccess', 'JSON Generated')}
                      subtitle={t('survey.submission.pact.jsonSuccessDescription', 'Your PACT PCF JSON was successfully generated and downloaded.')}
                      hideCloseButton
                      lowContrast
                    />
                  )}
                  
                  {jsonStatus === 'error' && (
                    <InlineNotification
                      kind="error"
                      title={t('survey.submission.pact.jsonError', 'JSON Generation Failed')}
                      subtitle={t('survey.submission.pact.jsonErrorDescription', 'There was an error generating your PACT PCF JSON. Please try again.')}
                      hideCloseButton
                      lowContrast
                    />
                  )}
                </div>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Tile>
        
        <div className="pact-note">
          <InlineNotification
            kind="info"
            title={t('survey.submission.pact.noteTitle', 'About PACT Format')}
            subtitle={t('survey.submission.pact.noteDescription', 'PACT (Pathfinder Accounting and Calculation Technology) is a standardized format for product carbon footprint data, enabling interoperability and transparency across the value chain.')}
            hideCloseButton
          />
        </div>
      </Column>
    </Grid>
  );
};

export default PactDownloads;